import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import './App.css';
import Admin from './Admin';

// Components
import { Button } from './components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { Input } from './components/ui/input';
import { Textarea } from './components/ui/textarea';
import { Badge } from './components/ui/badge';
import { Separator } from './components/ui/separator';
import { Phone, Mail, MapPin, Clock, Star, Users, Settings, Menu, X, ChevronLeft, ChevronRight } from 'lucide-react';

import ReCAPTCHA from 'react-google-recaptcha';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ScrollToTop Component - Scroll automatique en haut lors du changement de page
const ScrollToTop = () => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth' // Animation fluide
    });
  }, [location.pathname]); // Se déclenche à chaque changement de route

  return null;
};

// Bouton Retour en haut
const BackToTopButton = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const toggleVisibility = () => {
      if (window.pageYOffset > 300) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <>
      {isVisible && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 bg-orange-500 hover:bg-orange-600 text-white p-3 rounded-full shadow-lg transition-all z-50"
          aria-label="Retour en haut"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
          </svg>
        </button>
      )}
    </>
  );
};

// Header Component
const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const navigation = [
    { name: 'Accueil', href: '/' },
    { name: "L'Entreprise", href: '/entreprise' },
    { name: 'Parc de location', href: '/parc' },
    { name: 'Occasions', href: '/occasions' },
    { name: 'Réalisations', href: '/realisations' },
    { name: 'Installations', href: '/installations' },
    { name: 'Contact', href: '/contact' }
  ];

  const isActive = (href) => location.pathname === href;

  return (
    <header className="bg-white shadow-lg sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          <Link to="/" className="flex items-center relative z-10">
            <img 
              src="/uploads/f8912995-d297-43a7-a5a9-78884bd7a066.png" 
              alt="Logo TEKSAS" 
              className="h-24 w-auto object-contain -my-4 hover:scale-105 transition-transform duration-200"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-8">
            {navigation.map((nav) => (
              <Link
                key={nav.name}
                to={nav.href}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive(nav.href)
                    ? 'text-orange-600 bg-orange-50'
                    : 'text-gray-700 hover:text-orange-600 hover:bg-orange-50'
                }`}
              >
                {nav.name}
              </Link>
            ))}
          </nav>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2 rounded-md text-gray-700 hover:text-orange-600"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden pb-4">
            <nav className="flex flex-col space-y-2">
              {navigation.map((nav) => (
                <Link
                  key={nav.name}
                  to={nav.href}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive(nav.href)
                      ? 'text-orange-600 bg-orange-50'
                      : 'text-gray-700 hover:text-orange-600 hover:bg-orange-50'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {nav.name}
                </Link>
              ))}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <div className="w-10 h-10 flex items-center justify-center"> 
                <img 
                  src="/uploads/f8912995-d297-43a7-a5a9-78884bd7a066.png" 
                  alt="Logo TEKSAS" 
                  className="w-10 h-10 object-contain"
                />
	      </div>
              <span className="text-xl font-bold">TEKSAS</span>
            </div>
            <p className="text-gray-400 mb-4">
              Prestataire audiovisuel professionnel depuis 1998. 
              Location et prestations techniques pour tous vos événements.
            </p>
            <p className="text-gray-400 text-sm">
              SIRET : 934 878 828 00011<br/>
              RCS Metz B : 934 878 828<br/>
              TVA : FR30934878828
            </p>
          </div>
          
          <div>
            <h3 className="text-lg font-semibold mb-4">Nos Services</h3>
            <ul className="space-y-2 text-gray-400">
              <li>• Sonorisation professionnelle</li>
              <li>• Éclairage scénique</li>
              <li>• Solutions vidéo</li>
              <li>• Structures et podiums</li>
              <li>• Prestations techniques</li>
              <li>• Conseil et accompagnement</li>
            </ul>
          </div>
          
          <div>
            <h3 className="text-lg font-semibold mb-4">Contact</h3>
            <div className="space-y-3 text-gray-400">
              <div className="flex items-center space-x-2">
                <Phone size={16} />
                <span>+33 (0)3 87 32 56 19</span>
              </div>
              <div className="flex items-center space-x-2">
                <Mail size={16} />
                <span>contact@teksas.fr</span>
              </div>
              <div className="flex items-start space-x-2">
                <MapPin size={16} className="mt-0.5" />
                <div>
                  <p>8 Rue des Coûteliers</p>
                  <p>57070 Metz, France</p>
                </div>
              </div>
            </div>
            
            <div className="mt-6">
              <h4 className="text-md font-semibold mb-2">Horaires</h4>
              <div className="text-gray-400 text-sm">
                <p>Lun - Ven : 9h - 12h / 14h - 17h30</p>
                <p>Sam & Dim : Fermé</p>
              </div>
            </div>
          </div>
        </div>
        
        <Separator className="my-8 bg-gray-800" />
        
        <div className="flex flex-col md:flex-row justify-between items-center">
          <div className="text-center md:text-left mb-4 md:mb-0">
            <p className="text-gray-400">
              &copy; 202 Teksas. Tous droits réservés.
            </p>
          </div>
          
          <div className="flex space-x-6 text-sm text-gray-400">
            <a href="#" className="hover:text-orange-400 transition-colors">
              Mentions légales
            </a>
            <a href="#" className="hover:text-orange-400 transition-colors">
              Politique de confidentialité
            </a>
            <a href="#" className="hover:text-orange-400 transition-colors">
              CGV
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Carousel Component
const Carousel = ({ images }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prevIndex) => 
        prevIndex === images.length - 1 ? 0 : prevIndex + 1
      );
    }, 5000);

    return () => clearInterval(timer);
  }, [images.length]);

  const goToPrevious = () => {
    setCurrentIndex(currentIndex === 0 ? images.length - 1 : currentIndex - 1);
  };

  const goToNext = () => {
    setCurrentIndex(currentIndex === images.length - 1 ? 0 : currentIndex + 1);
  };

  if (!images || images.length === 0) return null;

  return (
    <div className="relative h-96 md:h-[500px] overflow-hidden rounded-lg">
      {images.map((image, index) => (
        <div
          key={image.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            index === currentIndex ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={image.image_url}
            alt={image.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black bg-opacity-30 flex items-end">
            <div className="p-8 text-white">
              <h3 className="text-2xl font-bold mb-2">{image.title}</h3>
              <p className="text-lg">{image.description}</p>
            </div>
          </div>
        </div>
      ))}
      
      <button
        onClick={goToPrevious}
        className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white p-2 rounded-full transition-all"
      >
        <ChevronLeft size={24} />
      </button>
      
      <button
        onClick={goToNext}
        className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white p-2 rounded-full transition-all"
      >
        <ChevronRight size={24} />
      </button>
      
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
        {images.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={`w-3 h-3 rounded-full transition-all ${
              index === currentIndex ? 'bg-white' : 'bg-white bg-opacity-50'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

// Home Page
const Home = () => {
  const [carouselImages, setCarouselImages] = useState([]);
  const [keyMetrics, setKeyMetrics] = useState([]);
  const [clients, setClients] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [carouselRes, metricsRes, clientsRes] = await Promise.all([
          axios.get(`${API}/carousel`),
          axios.get(`${API}/key-metrics`),
          axios.get(`${API}/clients`)
        ]);
        setCarouselImages(carouselRes.data);
        setKeyMetrics(metricsRes.data);
        setClients(clientsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, []);

  const getIcon = (iconName) => {
    const icons = {
      clock: Clock,
      star: Star,
      users: Users,
      settings: Settings
    };
    const IconComponent = icons[iconName] || Clock;
    return <IconComponent size={32} className="text-orange-500" />;
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section with Carousel */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Carousel images={carouselImages} />
        </div>
      </section>

      {/* Key Metrics */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              TEKSAS en chiffres
            </h2>
            <p className="text-xl text-gray-600">
              Plus de 26 ans d'expertise au service de vos événements
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {keyMetrics.map((metric) => (
              <Card key={metric.id} className="text-center hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex justify-center mb-4">
                    {getIcon(metric.icon)}
                  </div>
                  <div className="text-3xl font-bold text-orange-600 mb-2">
                    {metric.value}
                  </div>
                  <div className="text-gray-600">
                    {metric.label}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Nos Services
            </h2>
            <p className="text-xl text-gray-600">
              Solutions complètes pour tous vos besoins audiovisuels
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                title: "Sonorisation",
                description: "Système de sonorisation professionnel pour tous types d'événements",
                color: "from-yellow-400 to-orange-500"
              },
              {
                title: "Éclairage",
                description: "Solutions d'éclairage scénique et architectural",
                color: "from-yellow-400 to-orange-500"
              },
              {
                title: "Vidéo",
                description: "Équipement de projection et captation vidéo",
                color: "from-yellow-400 to-orange-500"
              },
              {
                title: "Structure",
                description: "Structures scéniques et podiums modulaires",
                color: "from-yellow-400 to-orange-500"
              }
            ].map((service, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow overflow-hidden">
                <div className={`h-2 bg-gradient-to-r ${service.color}`} />
                <CardHeader>
                  <CardTitle className="text-xl text-gray-900">
                    {service.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    {service.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Clients Section */}
      {clients.length > 0 && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Ils nous font confiance
              </h2>
              <p className="text-xl text-gray-600">
                Des clients satisfaits qui nous renouvellent leur confiance
              </p>
            </div>
            
            <div className="bg-white rounded-lg shadow-md p-8">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8 items-center">
                {clients.map((client) => (
                  <div key={client.id} className="flex items-center justify-center p-4">
                    {client.website ? (
                      <a 
                        href={client.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="hover:opacity-75 transition-opacity"
                        title={client.name}
                      >
                        <img
                          src={client.logo_url}
                          alt={client.name}
                          className="max-h-16 w-auto grayscale hover:grayscale-0 transition-all"
                        />
                      </a>
                    ) : (
                      <img
                        src={client.logo_url}
                        alt={client.name}
                        className="max-h-16 w-auto grayscale hover:grayscale-0 transition-all"
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-orange-500 to-yellow-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Prêt à organiser votre événement ?
          </h2>
          <p className="text-xl text-white mb-8">
            Contactez-nous pour un devis personnalisé et gratuit
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-orange-600 hover:bg-gray-100">
              Demander un devis
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

// Company Page
const Company = () => {
  return (
    <div className="min-h-screen py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-gray-900 mb-6">
            L'Entreprise TEKSAS
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Depuis 1998, TEKSAS accompagne vos événements avec passion et professionnalisme
          </p>
        </div>

        {/* History Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Notre Histoire</h2>
            <div className="space-y-4 text-gray-700">
              <p>
                Implantée à Metz Actipôle depuis 1998, notre société met son savoir-faire au service des structures culturelles de Lorraine et de l’Est de la France.
              </p>
              <p>
                Reconnus pour notre expertise dans le spectacle vivant, la conférence et l’éclairage architectural, nous offrons un accompagnement de proximité, porté par une structure à taille humaine et une parfaite connaissance du terrain.
              </p>
              <p>
                Animés par la passion de notre métier, nous disposons d’une équipe qualifiée capable d’intervenir en éclairage de théâtre, de concert, architectural, ainsi qu’en acoustique, électroacoustique et traitement du signal. Nos clients bénéficient chaque jour de prestations fiables, soutenues par l’utilisation d’outils modernes de mesure et d’analyse.
              </p>
            </div>
          </div>
          
          <div>
            <img 
              src="/uploads/ee6f4119-3997-4dda-81b5-22af2e68a4a3.jpg"
              alt="Équipement TEKSAS"
              className="w-full h-80 object-cover rounded-lg shadow-lg"
            />
          </div>
        </div>

        {/* Values Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Nos Valeurs</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: "Fiabilité",
                description: "Nous garantissons un matériel testé, des délais respectés et des solutions de secours pour assurer le bon déroulement de chaque événement."
              },
              {
                title: "Exigence de qualité",
                description: "Nous apportons un soin particulier au choix du matériel, aux réglages et aux finitions afin d’offrir un rendu sonore et visuel à la hauteur de vos attentes."
              },
              {
                title: "Proximité et écoute",
                description: "Nous prenons le temps de comprendre vos besoins pour vous proposer des solutions adaptées, avec un interlocuteur unique du premier contact jusqu’à l’événement."
              }
            ].map((value, index) => (
              <Card key={index} className="text-center">
                <CardHeader>
                  <CardTitle className="text-xl text-orange-600">
                    {value.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    {value.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Equipment Page
// Equipment Page
const Equipment = () => {
  const [equipment, setEquipment] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchEquipment = async () => {
      try {
        const response = await axios.get(`${API}/equipment`);
        setEquipment(response.data);
      } catch (error) {
        console.error('Error fetching equipment:', error);
      }
    };

    fetchEquipment();
  }, []);

  const categories = ['all', 'Sonorisation', 'Éclairage', 'Vidéo', 'Structure'];
  
  // Fonction de filtrage avec recherche et catégorie
  const getFilteredEquipment = () => {
    let filtered = equipment;

    // Filtrage par catégorie
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(eq => eq.category === selectedCategory);
    }

    // Filtrage par terme de recherche
    if (searchTerm.trim() !== '') {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(eq => 
        eq.name.toLowerCase().includes(searchLower) ||
        eq.category.toLowerCase().includes(searchLower) ||
        eq.description.toLowerCase().includes(searchLower) ||
        (eq.specifications && eq.specifications.toLowerCase().includes(searchLower))
      );
    }

    // Tri par catégorie d'abord, puis par description dans chaque catégorie
    filtered.sort((a, b) => {
      // D'abord comparer les catégories
      const categoryCompare = a.category.localeCompare(b.category);
      if (categoryCompare !== 0) {
        return categoryCompare;
      }
    
      // Si même catégorie, trier par description
      const descA = (a.description || '').toLowerCase();
      const descB = (b.description || '').toLowerCase();
      return descA.localeCompare(descB);
    });

    return filtered;
  };

  const filteredEquipment = getFilteredEquipment();

  // Fonction pour effacer la recherche
  const clearSearch = () => {
    setSearchTerm('');
  };

  // Fonction pour réinitialiser tous les filtres
  const resetAllFilters = () => {
    setSearchTerm('');
    setSelectedCategory('all');
  };

  return (
    <div className="min-h-screen py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-6">
            Parc de Location
          </h1>
          <p className="text-xl text-gray-600">
            Découvrez notre matériel professionnel disponible à la location
          </p>
        </div>

        {/* Barre de recherche */}
        <div className="mb-8">
          <div className="max-w-2xl mx-auto relative">
            <div className="relative">
              <input
                type="text"
                placeholder="Rechercher un équipement (nom, catégorie, description...)"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-3 pl-12 pr-12 text-gray-700 bg-white border-2 border-gray-200 rounded-lg focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-colors"
              />
              
              {/* Icône de recherche */}
              <div className="absolute inset-y-0 left-0 flex items-center pl-4">
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>

              {/* Bouton pour effacer la recherche */}
              {searchTerm && (
                <button
                  onClick={clearSearch}
                  className="absolute inset-y-0 right-0 flex items-center pr-4 text-gray-400 hover:text-gray-600 transition-colors"
                  title="Effacer la recherche"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>

            {/* Indicateur de résultats de recherche */}
            {searchTerm && (
              <div className="mt-2 text-sm text-gray-600 text-center">
                {filteredEquipment.length} résultat{filteredEquipment.length !== 1 ? 's' : ''} pour "{searchTerm}"
              </div>
            )}
          </div>
        </div>

        {/* Filtres par catégorie */}
        <div className="flex flex-wrap justify-center gap-4 mb-8">
          {categories.map((category) => (
            <Button
              key={category}
              variant={selectedCategory === category ? "default" : "outline"}
              onClick={() => setSelectedCategory(category)}
              className={`${selectedCategory === category ? "bg-orange-500 hover:bg-orange-600" : ""} relative`}
            >
              {category === 'all' ? 'Tous' : category}
              {selectedCategory === category && (
                <span className="ml-2 bg-white text-orange-500 rounded-full px-2 py-0.5 text-xs font-medium">
                  {category === 'all' ? equipment.length : equipment.filter(eq => eq.category === category).length}
                </span>
              )}
            </Button>
          ))}
        </div>

        {/* Bouton de réinitialisation (si des filtres sont actifs) */}
        {(searchTerm || selectedCategory !== 'all') && (
          <div className="text-center mb-6">
            <Button
              onClick={resetAllFilters}
              variant="outline"
              className="text-gray-600 hover:text-orange-600"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Réinitialiser les filtres
            </Button>
          </div>
        )}

        {/* Compteur de résultats */}
        <div className="text-center mb-6">
          <p className="text-gray-600">
            {filteredEquipment.length} équipement{filteredEquipment.length !== 1 ? 's' : ''} affiché{filteredEquipment.length !== 1 ? 's' : ''}
            {selectedCategory !== 'all' && ` dans la catégorie "${selectedCategory}"`}
          </p>
        </div>

        {/* Grille des équipements */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {filteredEquipment.map((item) => (
            <Card key={item.id} className="hover:shadow-lg transition-shadow">
              <div className="aspect-video bg-gray-200 rounded-t-lg overflow-hidden">
                <img
                  src={item.image_url}
                  alt={item.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <CardTitle className="text-lg">{item.name}</CardTitle>
                  <Badge variant="secondary">{item.category}</Badge>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">{item.description}</p>
                {item.specifications && (
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-sm text-gray-700 font-medium">Spécifications :</p>
                    <p className="text-sm text-gray-600 mt-1">{item.specifications}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Message si aucun résultat */}
        {filteredEquipment.length === 0 && (
          <div className="text-center py-12">
            <div className="mb-4">
              <svg className="w-16 h-16 text-gray-300 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun équipement trouvé</h3>
            <p className="text-gray-500 mb-4">
              {searchTerm 
                ? `Aucun équipement ne correspond à votre recherche "${searchTerm}"`
                : `Aucun équipement disponible dans la catégorie "${selectedCategory}"`
              }
            </p>
            <Button onClick={resetAllFilters} className="bg-orange-500 hover:bg-orange-600">
              Voir tous les équipements
            </Button>
          </div>
        )}

        {/* Section d'aide à la recherche */}
        {searchTerm && filteredEquipment.length === 0 && (
          <div className="max-w-2xl mx-auto mt-8 p-6 bg-blue-50 rounded-lg">
            <h4 className="font-medium text-blue-900 mb-2">💡 Conseils de recherche :</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Essayez des mots-clés plus généraux (ex: "micro" au lieu de "microphone")</li>
              <li>• Recherchez par catégorie (Sonorisation, Éclairage, Vidéo, Structure)</li>
              <li>• Vérifiez l'orthographe de votre recherche</li>
              <li>• Utilisez des termes techniques (ex: "LED", "console", "projecteur")</li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

// Realization Card Component with Image Carousel
const RealizationCard = ({ realization }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const images = realization.image_urls || [];
  const hasMultipleImages = images.length > 1;

  const goToPrevious = () => {
    setCurrentImageIndex((currentImageIndex - 1 + images.length) % images.length);
  };

  const goToNext = () => {
    setCurrentImageIndex((currentImageIndex + 1) % images.length);
  };

  return (
    <Card className="hover:shadow-lg transition-shadow overflow-hidden">
      <div className="aspect-video bg-gray-200 overflow-hidden relative group">
        {images.length > 0 ? (
          <>
            <img
              src={images[currentImageIndex]}
              alt={`${realization.title} - Image ${currentImageIndex + 1}`}
              className="w-full h-full object-cover"
            />
            
            {/* Navigation arrows - visible on hover if multiple images */}
            {hasMultipleImages && (
              <>
                <button
                  onClick={goToPrevious}
                  className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  onClick={goToNext}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <ChevronRight size={20} />
                </button>
                
                {/* Dots indicator */}
                <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex space-x-1">
                  {images.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentImageIndex(idx)}
                      className={`w-2 h-2 rounded-full transition-all ${
                        idx === currentImageIndex ? 'bg-white w-4' : 'bg-white bg-opacity-50'
                      }`}
                    />
                  ))}
                </div>
              </>
            )}
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400">
            Aucune image
          </div>
        )}
      </div>
      <CardHeader>
        <CardTitle className="text-lg">{realization.title}</CardTitle>
        <p className="text-sm text-gray-500">{realization.date}</p>
      </CardHeader>
      <CardContent>
        <p className="text-gray-600">{realization.description}</p>
      </CardContent>
    </Card>
  );
};

// Realizations Page
const Realizations = () => {
  const [realizations, setRealizations] = useState([]);

  useEffect(() => {
    const fetchRealizations = async () => {
      try {
        const response = await axios.get(`${API}/realizations`);
        setRealizations(response.data);
      } catch (error) {
        console.error('Error fetching realizations:', error);
      }
    };

    fetchRealizations();
  }, []);

  return (
    <div className="min-h-screen py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-6">
            Nos Réalisations
          </h1>
          <p className="text-xl text-gray-600">
            Découvrez quelques-unes de nos dernières prestations
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {realizations.map((realization) => (
            <RealizationCard key={realization.id} realization={realization} />
          ))}
        </div>

        {realizations.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">Aucune réalisation disponible pour le moment.</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Occasions Page (Coming Soon)
const Occasions = () => {
  return (
    <div className="min-h-screen py-16 flex items-center justify-center">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {/* Icon/Image */}
        <div className="mb-8">
          <div className="w-32 h-32 mx-auto bg-gradient-to-br from-orange-400 to-yellow-500 rounded-full flex items-center justify-center">
            <span className="text-white text-6xl">🔧</span>
          </div>
        </div>
        
        {/* Title */}
        <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
          Matériel d'Occasion
        </h1>
        
        {/* Subtitle */}
        <p className="text-xl sm:text-2xl text-gray-600 mb-8">
          Arrive bientôt ! 🚀
        </p>
        
        {/* Description */}
        <div className="bg-orange-50 rounded-lg p-8 mb-8">
          <p className="text-lg text-gray-700 mb-4">
            Nous préparons une sélection de matériel audiovisuel d'occasion 
            de qualité professionnelle à prix réduits.
          </p>
          <p className="text-gray-600">
            Cette section sera bientôt disponible avec des équipements 
            soigneusement vérifiés et garantis.
          </p>
        </div>
        
        {/* CTA */}
        <div className="space-y-4">
          <p className="text-gray-600">
            En attendant, n'hésitez pas à nous contacter pour toute demande.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-orange-500 hover:bg-orange-600">
              Nous contacter
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

// Contact Page
const Contact = () => {
  const [contactInfo, setContactInfo] = useState(null);
  const [faqs, setFaqs] = useState([]);
  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    email: '',
    event_date: '',
    description: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const [captchaValue, setCaptchaValue] = useState(null);
  const [captchaError, setCaptchaError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [contactRes, faqRes] = await Promise.all([
          axios.get(`${API}/contact-info`),
          axios.get(`${API}/faq`)
        ]);
        setContactInfo(contactRes.data);
        setFaqs(faqRes.data);
      } catch (error) {
        console.error('Error fetching contact data:', error);
      }
    };

    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');
    setCaptchaError('');

    if (!captchaValue) {
      setCaptchaError('Veuillez valider le captcha');
      setIsSubmitting(false);
      return;
    }

    try {
      await axios.post(`${API}/contact/submit`, formData);
      setSubmitMessage('Votre demande a été envoyée avec succès !');
      setFormData({
        firstname: '',
        lastname: '',
        email: '',
        event_date: '',
        description: ''
      });
      setCaptchaValue(null);
      // Reset du captcha
      if (window.grecaptcha) {
        window.grecaptcha.reset();
      }
    } catch (error) {
      setSubmitMessage('Erreur lors de l\'envoi. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-6">
            Contactez-nous
          </h1>
          <p className="text-xl text-gray-600">
            Demandez votre devis personnalisé en quelques clics
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          {/* Contact Form */}
          <Card>
            <CardHeader>
              <CardTitle>Demande de devis</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Prénom *
                    </label>
                    <Input
                      type="text"
                      name="firstname"
                      value={formData.firstname}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nom *
                    </label>
                    <Input
                      type="text"
                      name="lastname"
                      value={formData.lastname}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email *
                  </label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date de l'événement
                  </label>
                  <Input
                    type="date"
                    name="event_date"
                    value={formData.event_date}
                    onChange={handleChange}
                    placeholder="Optionnel"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description de votre événement *
                  </label>
                  <Textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={4}
                    placeholder="Décrivez votre événement, vos besoins techniques, le nombre d'invités..."
                    required
                  />
	       </div>

<div>

<div className="flex justify-center">
  <ReCAPTCHA
    sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
    onChange={(value) => {
      setCaptchaValue(value);
      setCaptchaError('');
    }}
    onExpired={() => {
      setCaptchaValue(null);
      setCaptchaError('Le captcha a expiré, veuillez le revalider');
    }}
  />
</div>

{/* Message d'erreur captcha */}
{captchaError && (
  <p className="text-red-600 text-sm text-center">{captchaError}</p>
)}
                </div>


                
                <Button 
                  type="submit" 
                  className="w-full bg-orange-500 hover:bg-orange-600"
                  disabled={isSubmitting || !captchaValue}
                >
                  {isSubmitting ? 'Envoi en cours...' : 'Envoyer la demande'}
                </Button>
                
                {submitMessage && (
                  <p className={`text-sm ${submitMessage.includes('succès') ? 'text-green-600' : 'text-red-600'}`}>
                    {submitMessage}
                  </p>
                )}
              </form>
            </CardContent>
          </Card>

          {/* Contact Info */}
          <div className="space-y-8">
            {contactInfo && (
              <Card>
                <CardHeader>
                  <CardTitle>Nos coordonnées</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <Phone className="text-orange-500" size={20} />
                    <span>{contactInfo.phone}</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Mail className="text-orange-500" size={20} />
                    <span>{contactInfo.email}</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <MapPin className="text-orange-500" size={20} />
                    <span>{contactInfo.address}</span>
                  </div>
                </CardContent>
              </Card>
            )}

            {contactInfo && (
              <Card>
                <CardHeader>
                  <CardTitle>Notre dépôt</CardTitle>
                </CardHeader>
                <CardContent>
                  <img
                    src={contactInfo.depot_image_url}
                    alt="Dépôt TEKSAS"
                    className="w-full h-48 object-cover rounded-lg"
                  />
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* FAQ Section */}
        {faqs.length > 0 && (
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              Questions Fréquentes
            </h2>
            <div className="max-w-3xl mx-auto space-y-4">
              {faqs.map((faq) => (
                <Card key={faq.id}>
                  <CardHeader>
                    <CardTitle className="text-lg">{faq.question}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


// Installations Page
const Installations = () => {
  const [installations, setInstallations] = useState([]);
  const [brands, setBrands] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [installationsRes, brandsRes] = await Promise.all([
          axios.get(`${API}/installations`),
          axios.get(`${API}/brands`)
        ]);
        setInstallations(installationsRes.data);
        setBrands(brandsRes.data);
      } catch (error) {
        console.error('Error fetching installation data:', error);
      }
    };

    fetchData();
  }, []);

  const categories = ['all', 'Bar & Restaurant', 'Salles', 'Autre'];
  
  const filteredInstallations = selectedCategory === 'all' 
    ? installations 
    : installations.filter(inst => inst.category === selectedCategory);

  return (
    <div className="min-h-screen py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            Installation en Fixe
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Solutions audiovisuelles clés en main pour vos établissements professionnels
          </p>
        </div>

        {/* Services Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Nos Services</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: '🍽️',
                title: 'Restaurants',
                description: 'Ambiance sonore, diffusion musicale, écrans TV'
              },
              {
                icon: '🍺',
                title: 'Bars & Pubs',
                description: 'Sonorisation, éclairage d\'ambiance, diffusion sportive'
              },
              {
                icon: '🎭',
                title: 'Salles',
                description: 'Installation complète son, lumière et vidéo'
              },
              {
                icon: '🏨',
                title: 'Autres',
                description: 'Salles de conférence, espaces événementiels'
              }
            ].map((service, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="text-5xl mb-4">{service.icon}</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {service.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* What We Do Section */}
        <div className="bg-gradient-to-r from-orange-50 to-yellow-50 rounded-lg p-8 mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Ce que nous faisons</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div>
              <h3 className="text-xl font-semibold text-orange-600 mb-3">🔧 Installation complète</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Étude de vos besoins</li>
                <li>• Conception sur mesure</li>
                <li>• Installation et câblage</li>
                <li>• Formation à l'utilisation</li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-orange-600 mb-3">⚙️ Maintenance & Support</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Maintenance préventive</li>
                <li>• Support technique</li>
                <li>• Mise à jour système</li>
                <li>• Garantie équipements</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Installations Gallery */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Nos Installations</h2>
          
          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {categories.map((category) => (
              <Button
                key={category}
                variant={selectedCategory === category ? "default" : "outline"}
                onClick={() => setSelectedCategory(category)}
                className={selectedCategory === category ? "bg-orange-500 hover:bg-orange-600" : ""}
              >
                {category === 'all' ? 'Toutes' : category}
              </Button>
            ))}
          </div>

          {/* Gallery Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredInstallations.map((installations) => (
              <Card key={installations.id} className="hover:shadow-lg transition-shadow overflow-hidden">
                <div className="aspect-video bg-gray-200 overflow-hidden">
                  <img
                    src={installations.image_url}
                    alt={installations.title}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <CardTitle className="text-lg">{installations.title}</CardTitle>
                    <Badge variant="secondary">{installations.category}</Badge>
                  </div>
                  <p className="text-sm text-gray-500 flex items-center">
                    <MapPin size={14} className="mr-1" />
                    {installations.location}
                  </p>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">{installations.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {filteredInstallations.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">Aucune installation dans cette catégorie.</p>
            </div>
          )}
        </div>

        {/* Brands Section */}
        {brands.length > 0 && (
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Nos Marques Partenaires</h2>
            <div className="bg-white rounded-lg shadow-md p-8">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8 items-center">
                {brands.map((brand) => (
                  <div key={brand.id} className="flex items-center justify-center p-4">
                    {brand.website ? (
                      <a 
                        href={brand.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="hover:opacity-75 transition-opacity"
                      >
                        <img
                          src={brand.logo_url}
                          alt={brand.name}
                          className="max-h-16 w-auto grayscale hover:grayscale-0 transition-all"
                        />
                      </a>
                    ) : (
                      <img
                        src={brand.logo_url}
                        alt={brand.name}
                        className="max-h-16 w-auto grayscale hover:grayscale-0 transition-all"
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-orange-500 to-yellow-500 rounded-lg p-12 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Un projet d'installation ?
          </h2>
          <p className="text-xl text-white mb-8">
            Contactez-nous pour une étude personnalisée et un devis gratuit
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-orange-600 hover:bg-gray-100">
              Demander un devis
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

// Main App Component
function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <Header />
        <main>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/entreprise" element={<Company />} />
            <Route path="/parc" element={<Equipment />} />
            <Route path="/occasions" element={<Occasions />} />
            <Route path="/realisations" element={<Realizations />} />
            <Route path="/installations" element={<Installations />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/admin" element={<Admin />} />
          </Routes>
        </main>
        <Footer />
        <BackToTopButton />
      </BrowserRouter>
    </div>
  );
}

export default App;
